import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraDataTable, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraCheckbox, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraInlineBanner, SpecraOverlayFooter } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
import { useFillPricingWorkbook, usePriceVolume } from './usePriceVolume';
const T = Testids.pricingWorkspace;
export function FillWorkbookDialog({ scope, workbookId, categories, periods, onClose, onFilled }) {
    const evidence = usePriceVolume(scope);
    const cache = useQueryClient();
    const fill = useFillPricingWorkbook();
    const periodFieldId = useId();
    const [version, setVersion] = useState('');
    const [selected, setSelected] = useState(()=>periods.map((p)=>p.periodKey));
    const [preview, setPreview] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const generation = useRef(0);
    const previewBusy = useRef(false);
    const fillBusy = useRef(false);
    useEffect(()=>()=>{
            generation.current++;
        }, []);
    const close = ()=>{
        if (fillBusy.current) return;
        generation.current++;
        onClose();
    };
    const change = ()=>{
        generation.current++;
        previewBusy.current = false;
        setPreview(undefined);
        setLoading(false);
        setError('');
        fill.reset();
    };
    const versions = evidence.data?.versions ?? [];
    const nextVersion = Math.max(0, ...versions.map((v)=>v.versionNo)) + 1;
    const category = (id)=>categories.find((c)=>c.id === id)?.title || 'Category unavailable';
    const period = (key)=>periods.find((p)=>p.periodKey === key)?.label || key;
    const requestPreview = async ()=>{
        if (!selected.length || previewBusy.current || fillBusy.current || preview) return;
        const request = ++generation.current;
        const periodKeys = [
            ...selected
        ];
        previewBusy.current = true;
        setLoading(true);
        setError('');
        try {
            const result = await projectDataClient.previewPricingWorkbookFill({
                projectId: scope.projectId,
                workbookId,
                scenarioVersionId: version,
                periodKeys
            });
            if (request !== generation.current) return;
            if (!result.scenarioVersionId) throw new Error('Preview did not return a frozen pricing version. Retry preview.');
            setVersion(result.scenarioVersionId);
            setPreview({
                result,
                periodKeys
            });
        } catch (e) {
            if (request === generation.current) setError(ConnectError.from(e).rawMessage);
        } finally{
            // Preview can freeze a version even if a later read fails or this dialog closes.
            void cache.invalidateQueries({
                queryKey: [
                    'pricing-evidence'
                ]
            });
            if (request === generation.current) {
                previewBusy.current = false;
                setLoading(false);
            }
        }
    };
    const confirm = async ()=>{
        if (!preview || !selected.length || fillBusy.current || loading) return;
        fillBusy.current = true;
        const request = generation.current;
        try {
            const result = await fill.mutateAsync({
                projectId: scope.projectId,
                workbookId,
                scenarioVersionId: preview.result.scenarioVersionId,
                periodKeys: [
                    ...preview.periodKeys
                ]
            });
            if (request === generation.current) onFilled(result);
        } catch  {
        // Keep the preview's frozen version and periods for an idempotent retry.
        } finally{
            fillBusy.current = false;
        }
    };
    return /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
        open: true,
        size: "xl",
        title: "Fill from pricing",
        testId: T.priceVolumeFillDialog,
        onOpenChange: (open)=>{
            if (!open) close();
        },
        footer: /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
            onCancel: close,
            cancelProps: {
                disabled: fill.isPending
            },
            onConfirm: confirm,
            confirmLabel: fill.isError ? 'Retry fill' : 'Fill workbook',
            confirmDisabled: !preview || !selected.length || loading,
            loading: fill.isPending,
            loadingLabel: "Filling…",
            confirmProps: {
                'data-testid': T.priceVolumeFillConfirm
            }
        }),
        children: [
            /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "info",
                children: "Anyone with the workbook open will be asked to reload."
            }),
            evidence.isPending && /*#__PURE__*/ _jsx(SpecraText, {
                children: "Loading pricing versions…"
            }),
            evidence.isError && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    "Pricing versions unavailable.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>evidence.refetch(),
                        children: "Retry versions"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraSelect, {
                "aria-label": "Pricing version",
                value: version,
                disabled: fill.isPending || evidence.isPending || evidence.isError,
                onChange: (e)=>{
                    change();
                    setVersion(e.target.value);
                },
                children: [
                    /*#__PURE__*/ _jsxs("option", {
                        value: "",
                        children: [
                            "Freeze now ",
                            evidence.data ? `(v${nextVersion})` : '(version unavailable)'
                        ]
                    }),
                    version && !versions.some((v)=>v.versionId === version && v.frozen) && /*#__PURE__*/ _jsx("option", {
                        value: version,
                        children: "Frozen pricing version (number unavailable)"
                    }),
                    versions.filter((v)=>v.frozen).map((v)=>/*#__PURE__*/ _jsxs("option", {
                            value: v.versionId,
                            children: [
                                "Pricing v",
                                v.versionNo
                            ]
                        }, v.versionId))
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                children: "Periods"
            }),
            periods.map((p)=>/*#__PURE__*/ _jsx(SpecraFormField, {
                    label: p.label,
                    htmlFor: `${periodFieldId}-${p.periodKey}`,
                    children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                        id: `${periodFieldId}-${p.periodKey}`,
                        "aria-label": p.label,
                        checked: selected.includes(p.periodKey),
                        disabled: fill.isPending,
                        onCheckedChange: (checked)=>{
                            change();
                            setSelected((old)=>checked === true ? [
                                    ...old,
                                    p.periodKey
                                ] : old.filter((key)=>key !== p.periodKey));
                        }
                    })
                }, p.periodKey)),
            !selected.length && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                children: "Select at least one mapped period to preview and fill."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": T.priceVolumeFillPreview,
                onClick: requestPreview,
                disabled: !selected.length || loading || fill.isPending || !!preview || !evidence.data || evidence.isError,
                children: loading ? 'Previewing…' : 'Preview fill'
            }),
            (error || fill.error) && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: error || ConnectError.from(fill.error).rawMessage
            }),
            preview && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Preview uses",
                            ' ',
                            versions.find((v)=>v.versionId === preview.result.scenarioVersionId)?.versionNo ? `pricing v${versions.find((v)=>v.versionId === preview.result.scenarioVersionId)?.versionNo}` : 'a frozen pricing version (version number unavailable)',
                            ". Confirm and retry use this same version."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            preview.result.changedCells.length,
                            " changed cells. Fill writes all eligible mapped cells in the selected periods, including unchanged values."
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraDataTable, {
                        testId: T.priceVolumeFillDiff,
                        rows: preview.result.changedCells,
                        getRowId: (c)=>`${c.sheet}!${c.cell}`,
                        emptyTitle: "No changed cells",
                        emptyDescription: "Eligible mapped cells may already match. Skipped cells are not written.",
                        columns: [
                            {
                                id: 'category',
                                header: 'Category',
                                cell: (c)=>category(c.categoryId)
                            },
                            {
                                id: 'period',
                                header: 'Period',
                                cell: (c)=>period(c.periodKey)
                            },
                            {
                                id: 'cell',
                                header: 'Cell',
                                cell: (c)=>`${c.sheet}!${c.cell}`
                            },
                            {
                                id: 'current',
                                header: 'Current workbook value',
                                cell: (c)=>c.currentValue === '' ? '(empty)' : c.currentValue
                            },
                            {
                                id: 'new',
                                header: 'New value',
                                cell: (c)=>c.newValue
                            }
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Skipped: ",
                            preview.result.skipped.length || 'none'
                        ]
                    }),
                    preview.result.skipped.map((s, i)=>/*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                            tone: "warning",
                            children: [
                                category(s.categoryId),
                                " · ",
                                period(s.periodKey),
                                ":",
                                ' ',
                                s.reason === 'no_rate' ? 'No Using rate (no_rate)' : s.reason === 'no_hours' ? 'No hours (no_hours)' : s.reason
                            ]
                        }, `${s.categoryId}-${s.periodKey}-${i}`))
                ]
            })
        ]
    });
}
